import getDataSelector from 'core/utils/getDataSelector'
import { createSharedSelector } from 'core/utils/selectorHelpers'
import DataKeys from 'k8s/DataKeys'
import { ServiceState, ServiceStatus } from './constant'
import { mergeHosts } from './helper'

export { ServiceState, ServiceStatus }

import {
  blueprintsSelector,
  resmgrHostsSelector,
  UNASSIGNED_CLUSTER,
} from 'app/plugins/openstack/components/infrastructure/selectors'

//Helper Functions

const REQUIRED_BINARIES = {
  Compute: ['nova-compute'],
  Storage: ['cinder-volume'],
  Network: ['ovn-controller'],
}

const getConnectivityStatus = (host: any) => {
  if (host?.info?.responding === undefined || host?.info?.responding) return 'online'

  if (!host?.info?.responding && host?.roles?.length === 0) {
    return 'unauthorized'
  }
  return 'offline'
}

const getServiceStatus = (service: any) => {
  return service?.state === ServiceState.Up
    ? ServiceStatus.Healthy
    : service?.state === ServiceState.CertificateError
      ? ServiceStatus.CertificateError
      : service?.state === ServiceState.NetworkError
        ? ServiceStatus.NetworkError
        : ServiceStatus.Unhealthy
}

export const getIPAddresses = (host: any) => {
  return Object.entries(host?.extensions?.interfaces?.data?.iface_info || {}).flatMap(
    ([iface, info]: any) =>
      info?.ifaces?.map((_iface) => ({
        name: iface,
        ip: _iface.addr,
      })),
  )
}

export const getRoleStatus = (host: any) => {
  return host?.role_status === 'ok'
    ? 'Configured'
    : host?.role_status
      ? host?.role_status
      : 'Not Configured'
}

export const computeServicesSelector = createSharedSelector(
  getDataSelector<DataKeys.ComputeServiceDetails>(DataKeys.ComputeServiceDetails),
  resmgrHostsSelector,
  (computeServices, hosts) => {
    const filteredComputeServices = computeServices.filter((item) =>
      REQUIRED_BINARIES.Compute.includes(item.binary),
    )
    return filteredComputeServices.map((computeService) => {
      const host = hosts.find((host) => computeService.host.includes(host.id))
      const name = host?.info?.hostname || computeService?.host
      const clusterName = host?.clusterName || UNASSIGNED_CLUSTER
      const ipAddresses = getIPAddresses(host)
      const status = host?.scheduling?.service?.disabled_reason
        ? ServiceStatus.Maintenance
        : host?.schedulingStatus === 'Disabled'
          ? ServiceStatus.SchedulingDisabled
          : getServiceStatus(computeService)
      const connectivityStatus = getConnectivityStatus(host)
      const roleStatus = getRoleStatus(host)
      return {
        ...computeService,
        hostDetails: host,
        name,
        status,
        computeServicesStatus: status,
        clusterName,
        ipAddresses,
        connectivityStatus,
        roleStatus,
      }
    })
  },
)

export const storageServicesSelector = createSharedSelector(
  getDataSelector<DataKeys.StorageServiceDetails>(DataKeys.StorageServiceDetails),
  resmgrHostsSelector,
  (storageServices, hosts) => {
    // Filter out disabled services and services that are not required
    const filteredStorageServices = storageServices.filter(
      (item) => REQUIRED_BINARIES.Storage.includes(item.binary) && item?.status === 'enabled',
    )
    return filteredStorageServices.map((storageService) => {
      const host = hosts.find((host) => storageService.host.includes(host.id))
      const serviceHostParts = storageService?.host?.split('@') || []
      const volumeBackend = serviceHostParts[serviceHostParts.length - 1]
      const name = host?.info?.hostname || storageService?.host
      const status = getServiceStatus(storageService)
      const clusterName = host?.clusterName || UNASSIGNED_CLUSTER
      const ipAddresses = getIPAddresses(host)
      const connectivityStatus = getConnectivityStatus(host)
      const roleStatus = getRoleStatus(host)
      return {
        ...storageService,
        id: storageService?.host,
        hostDetails: host,
        name,
        status,
        storageServiceStatus: status,
        clusterName,
        ipAddresses,
        connectivityStatus,
        roleStatus,
        volumeBackend,
      }
    })
  },
)

export const allStorageServicesSelector = createSharedSelector(
  getDataSelector<DataKeys.StorageServiceDetails>(DataKeys.StorageServiceDetails),
  resmgrHostsSelector,
  (storageServices, hosts) => {
    // Filter out disabled services and services that are not required
    const filteredStorageServices = storageServices.filter((item) =>
      REQUIRED_BINARIES.Storage.includes(item.binary),
    )
    return filteredStorageServices.map((storageService) => {
      const host = hosts.find((host) => storageService.host.includes(host.id))
      const serviceHostParts = storageService?.host?.split('@') || []
      const volumeBackend = serviceHostParts[serviceHostParts.length - 1]
      const name = host?.info?.hostname || storageService?.host
      const status = getServiceStatus(storageService)
      const clusterName = host?.clusterName || UNASSIGNED_CLUSTER
      const ipAddresses = getIPAddresses(host)
      const connectivityStatus = getConnectivityStatus(host)
      const roleStatus = getRoleStatus(host)
      return {
        ...storageService,
        id: storageService?.host,
        hostDetails: host,
        name,
        status,
        storageServiceStatus: status,
        // `status` above is the health status; this keeps the os-services enabled/disabled value
        schedulingStatus: storageService?.status,
        clusterName,
        ipAddresses,
        connectivityStatus,
        roleStatus,
        volumeBackend,
      }
    })
  },
)

export const networkServicesSelector = createSharedSelector(
  getDataSelector<DataKeys.NetworkServiceDetails>(DataKeys.NetworkServiceDetails),
  resmgrHostsSelector,
  (networkServices, hosts) => {
    const filteredNetworkServices = networkServices.filter((item) =>
      REQUIRED_BINARIES.Network.includes(item.binary),
    )
    return filteredNetworkServices.map((networkService) => {
      const host = hosts.find((host) => networkService.host.includes(host.id))
      const name = host?.info?.hostname || networkService?.host
      const status = networkService?.alive ? ServiceStatus.Healthy : ServiceStatus.Unhealthy
      const clusterName = host?.clusterName || UNASSIGNED_CLUSTER
      const ipAddresses = getIPAddresses(host)
      const connectivityStatus = getConnectivityStatus(host)
      const roleStatus = getRoleStatus(host)
      return {
        ...networkService,
        hostDetails: host,
        name,
        status,
        networkServiceStatus: status,
        clusterName,
        ipAddresses,
        connectivityStatus,
        roleStatus,
      }
    })
  },
)

export const imageServicesSelector = createSharedSelector(
  getDataSelector<DataKeys.ImageServiceDetails>(DataKeys.ImageServiceDetails),
  resmgrHostsSelector,
  blueprintsSelector,
  (imageServices, hosts, blueprints) => {
    // PCD-9693: once certificatesKeyPath/glanceCertificateSan are configured, Glance
    // re-registers a host under <hostname>.<glanceCertificateSan> (an FQDN) instead of an IP,
    // so the IP-based interface match below never resolves for that host. The SAN suffix is
    // known from the blueprint, so strip it (rather than guessing where the hostname ends) to
    // recover the real hostname and match that against the host's own hostname. Hosts without
    // a certificate still register by IP, so that match is tried first/always.
    const glanceCertificateSan = blueprints?.[0]?.glanceCertificateSan
    return imageServices.map((imageService) => {
      const serviceHost = new URL(imageService?.host).host.split(':')[0]
      const sanSuffix = glanceCertificateSan ? `.${glanceCertificateSan}` : null
      const serviceHostname =
        sanSuffix && serviceHost.endsWith(sanSuffix)
          ? serviceHost.slice(0, -sanSuffix.length)
          : null
      const host = hosts.find(
        (host) =>
          (host?.roles?.includes('image-library') ||
            host?.v1Roles?.includes('pf9-glance-role-vmw')) &&
          (Object.values(host?.extensions?.interfaces?.data?.iface_info || {}).some((iface: any) =>
            iface?.ifaces?.some((i: any) => i.addr === serviceHost),
          ) ||
            (!!serviceHostname && host?.info?.hostname === serviceHostname)),
      )
      const name = host?.info?.hostname || imageService?.host
      const status = getServiceStatus(imageService)
      const clusterName = host?.clusterName || UNASSIGNED_CLUSTER
      const ipAddresses = getIPAddresses(host)
      const connectivityStatus = getConnectivityStatus(host)
      const roleStatus = getRoleStatus(host)
      return {
        ...imageService,
        hostDetails: host,
        name,
        status,
        imageServiceStatus: status,
        clusterName,
        ipAddresses,
        connectivityStatus,
        roleStatus,
      }
    })
  },
)

// Cached single-row metadata health summary counts (total networks + working ports).
// See MetadataServiceHealth.spec.md (MSH-FR-003).
export const metadataServiceHealthSelector = getDataSelector<DataKeys.MetadataServiceHealth>(
  DataKeys.MetadataServiceHealth,
)

// Cached list of networks missing their distributed (metadata) port, loaded on demand
// when the card is expanded. See MetadataServiceHealth.spec.md (MSH-FR-004).
export const brokenMetadataNetworksSelector = getDataSelector<DataKeys.BrokenMetadataNetworks>(
  DataKeys.BrokenMetadataNetworks,
)

export const combinedServicesSelector = createSharedSelector(
  computeServicesSelector,
  storageServicesSelector,
  networkServicesSelector,
  imageServicesSelector,
  (computeServices, storageServices, networkServices, imageServices) => {
    const filteredCombinedServices = mergeHosts([
      computeServices,
      storageServices,
      networkServices,
      imageServices,
    ])
    return filteredCombinedServices.map((combinedServices) => {
      return {
        ...combinedServices,
      }
    })
  },
)
